import React, { useEffect, useState } from "react";
import { PortalHeader, PortalBody, EmptyState, StatusPill } from "@/components/PortalUI";
import api, { formatApiError } from "@/lib/api";
import { Plus, Radio } from "lucide-react";

export default function AdminLeadExchange() {
  const [tab, setTab] = useState("leads");
  return (
    <>
      <PortalHeader eyebrow="/ ADMIN · INTERNAL OPS" title="Lead exchange" testId="admin-lx-header">
        <div className="flex gap-1">
          {["leads","buyers","sources","rules","ledger"].map((t) => (
            <button key={t} className={`dt6-btn text-xs ${tab===t?"dt6-btn-primary":""}`} onClick={()=>setTab(t)} data-testid={`lx-tab-${t}`}>{t}</button>
          ))}
        </div>
      </PortalHeader>
      <PortalBody>
        <div className="mb-4 text-xs text-[color:var(--dt6-muted)] font-mono tracking-[0.14em] uppercase">
          / EXCHANGE V1 · INTERNAL OPERATIONS. SELF-SERVE MARKETPLACE = PHASE 2.
        </div>
        {tab === "leads" && <LeadsTab />}
        {tab === "buyers" && <BuyersTab />}
        {tab === "sources" && <SourcesTab />}
        {tab === "rules" && <RulesTab />}
        {tab === "ledger" && <LedgerTab />}
      </PortalBody>
    </>
  );
}

function LeadsTab() {
  const [rows, setRows] = useState([]);
  const [lead, setLead] = useState({ lead_type: "supporter", contact: {}, metadata: {} });
  const [err, setErr] = useState("");
  const load = () => api.get("/admin/lead-exchange/leads").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    setErr("");
    try { await api.post("/admin/lead-exchange/leads", lead); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const route = async (id) => {
    try { await api.post(`/admin/lead-exchange/leads/${id}/route`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  return (
    <>
      <div className="dt6-card p-4 mb-4">
        <div className="dt6-chip mb-3">+ NEW LEAD</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select className="dt6-select" value={lead.lead_type} onChange={(e)=>setLead({...lead,lead_type:e.target.value})} data-testid="lx-lead-type">
            {["supporter","volunteer","donor","petition"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input className="dt6-input" placeholder="name" value={lead.contact.name || ""} onChange={(e)=>setLead({...lead,contact:{...lead.contact,name:e.target.value}})} data-testid="lx-lead-name" />
          <input className="dt6-input" placeholder="email" value={lead.contact.email || ""} onChange={(e)=>setLead({...lead,contact:{...lead.contact,email:e.target.value}})} data-testid="lx-lead-email" />
          <button className="dt6-btn dt6-btn-primary" onClick={create} data-testid="lx-lead-create"><Plus size={12} /> Add lead</button>
        </div>
      </div>
      {err && <div className="text-sm text-[color:var(--dt6-danger)] mb-3">{err}</div>}
      {rows.length === 0 ? (
        <EmptyState testId="lx-leads-empty">NO LEADS.</EmptyState>
      ) : (
        <div className="dt6-card overflow-hidden">
          <table className="dt6-table">
            <thead><tr><th>Type</th><th>Contact</th><th>Status</th><th>Created</th><th></th></tr></thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} data-testid={`lx-lead-row-${l.id}`}>
                  <td><span className="font-mono">{l.lead_type}</span></td>
                  <td className="text-xs">{(l.contact?.name || "—") + " · " + (l.contact?.email || "—")}</td>
                  <td><StatusPill status={l.status} /></td>
                  <td className="font-mono text-xs text-[color:var(--dt6-muted)]">{new Date(l.created_at).toLocaleString()}</td>
                  <td>
                    {l.status !== "routed" && (
                      <button className="dt6-btn text-xs" onClick={() => route(l.id)} data-testid={`lx-route-${l.id}`}>
                        <Radio size={12} /> Route
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function BuyersTab() {
  const [rows, setRows] = useState([]);
  const [b, setB] = useState({ name: "", contact_email: "", lead_types: "supporter,donor", price_per_lead: 3.5, active: true });
  const load = () => api.get("/admin/lead-exchange/buyers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    const payload = { ...b, lead_types: b.lead_types.split(",").map(s=>s.trim()).filter(Boolean), price_per_lead: parseFloat(b.price_per_lead) };
    await api.post("/admin/lead-exchange/buyers", payload);
    setB({ name: "", contact_email: "", lead_types: "supporter,donor", price_per_lead: 3.5, active: true });
    await load();
  };
  return (
    <>
      <div className="dt6-card p-4 mb-4">
        <div className="dt6-chip mb-3">+ NEW BUYER</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="dt6-input" placeholder="name" value={b.name} onChange={(e)=>setB({...b,name:e.target.value})} data-testid="lx-buyer-name" />
          <input className="dt6-input" type="email" placeholder="contact email" value={b.contact_email} onChange={(e)=>setB({...b,contact_email:e.target.value})} data-testid="lx-buyer-email" />
          <input className="dt6-input" placeholder="lead types (comma)" value={b.lead_types} onChange={(e)=>setB({...b,lead_types:e.target.value})} data-testid="lx-buyer-types" />
          <input className="dt6-input" type="number" step="0.1" placeholder="$ / lead" value={b.price_per_lead} onChange={(e)=>setB({...b,price_per_lead:e.target.value})} data-testid="lx-buyer-price" />
        </div>
        <div className="mt-3 flex justify-end"><button className="dt6-btn dt6-btn-primary text-xs" onClick={create} disabled={!b.name || !b.contact_email} data-testid="lx-buyer-add"><Plus size={12} /> Add buyer</button></div>
      </div>
      {rows.length === 0 ? (
        <EmptyState testId="lx-buyers-empty">NO BUYERS.</EmptyState>
      ) : (
        <div className="dt6-card overflow-hidden">
          <table className="dt6-table">
            <thead><tr><th>Name</th><th>Email</th><th>Types</th><th>$/lead</th><th>Active</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`lx-buyer-row-${r.id}`}>
                  <td>{r.name}</td><td className="text-xs">{r.contact_email}</td>
                  <td className="text-xs font-mono">{(r.lead_types || []).join(",")}</td>
                  <td className="font-mono">${r.price_per_lead}</td>
                  <td>{r.active ? "yes" : "no"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function SourcesTab() {
  const [rows, setRows] = useState([]);
  const [s, setS] = useState({ name: "", lead_type: "supporter", notes: "", active: true });
  const load = () => api.get("/admin/lead-exchange/sources").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => { await api.post("/admin/lead-exchange/sources", s); setS({ name: "", lead_type: "supporter", notes: "", active: true }); await load(); };
  return (
    <>
      <div className="dt6-card p-4 mb-4">
        <div className="dt6-chip mb-3">+ NEW SOURCE</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="dt6-input" placeholder="name" value={s.name} onChange={(e)=>setS({...s,name:e.target.value})} data-testid="lx-src-name" />
          <select className="dt6-select" value={s.lead_type} onChange={(e)=>setS({...s,lead_type:e.target.value})} data-testid="lx-src-type">
            {["supporter","volunteer","donor","petition"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input className="dt6-input md:col-span-2" placeholder="notes" value={s.notes} onChange={(e)=>setS({...s,notes:e.target.value})} data-testid="lx-src-notes" />
        </div>
        <div className="mt-3 flex justify-end"><button className="dt6-btn dt6-btn-primary text-xs" onClick={create} disabled={!s.name} data-testid="lx-src-add"><Plus size={12} /> Add source</button></div>
      </div>
      {rows.length === 0 ? (
        <EmptyState testId="lx-sources-empty">NO SOURCES.</EmptyState>
      ) : (
        <div className="dt6-card overflow-hidden">
          <table className="dt6-table">
            <thead><tr><th>Name</th><th>Type</th><th>Notes</th><th>Active</th></tr></thead>
            <tbody>{rows.map((r)=>(<tr key={r.id} data-testid={`lx-src-row-${r.id}`}><td>{r.name}</td><td className="font-mono">{r.lead_type}</td><td className="text-xs">{r.notes || "—"}</td><td>{r.active ? "yes" : "no"}</td></tr>))}</tbody>
          </table>
        </div>
      )}
    </>
  );
}

function RulesTab() {
  const [rows, setRows] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [r, setR] = useState({ name: "", lead_type: "supporter", buyer_id: "", priority: 100, active: true, conditions: {} });
  const load = async () => {
    setRows((await api.get("/admin/lead-exchange/rules")).data);
    setBuyers((await api.get("/admin/lead-exchange/buyers")).data);
  };
  useEffect(() => { load(); }, []);
  const create = async () => { await api.post("/admin/lead-exchange/rules", r); setR({ name: "", lead_type: "supporter", buyer_id: "", priority: 100, active: true, conditions: {} }); await load(); };
  return (
    <>
      <div className="dt6-card p-4 mb-4">
        <div className="dt6-chip mb-3">+ NEW ROUTING RULE</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="dt6-input" placeholder="rule name" value={r.name} onChange={(e)=>setR({...r,name:e.target.value})} data-testid="lx-rule-name" />
          <select className="dt6-select" value={r.lead_type} onChange={(e)=>setR({...r,lead_type:e.target.value})} data-testid="lx-rule-type">
            {["supporter","volunteer","donor","petition"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="dt6-select" value={r.buyer_id} onChange={(e)=>setR({...r,buyer_id:e.target.value})} data-testid="lx-rule-buyer">
            <option value="">— select buyer —</option>
            {buyers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <input className="dt6-input" type="number" placeholder="priority" value={r.priority} onChange={(e)=>setR({...r,priority:parseInt(e.target.value)})} data-testid="lx-rule-priority" />
        </div>
        <div className="mt-3 flex justify-end"><button className="dt6-btn dt6-btn-primary text-xs" disabled={!r.name || !r.buyer_id} onClick={create} data-testid="lx-rule-add"><Plus size={12} /> Add rule</button></div>
      </div>
      {rows.length === 0 ? (
        <EmptyState testId="lx-rules-empty">NO RULES.</EmptyState>
      ) : (
        <div className="dt6-card overflow-hidden">
          <table className="dt6-table">
            <thead><tr><th>Name</th><th>Type</th><th>Buyer</th><th>Priority</th><th>Active</th></tr></thead>
            <tbody>{rows.map((rr) => {
              const buyer = buyers.find((b) => b.id === rr.buyer_id);
              return <tr key={rr.id} data-testid={`lx-rule-row-${rr.id}`}><td>{rr.name}</td><td className="font-mono">{rr.lead_type}</td><td className="text-xs">{buyer?.name || rr.buyer_id.slice(0,8)}</td><td className="font-mono">{rr.priority}</td><td>{rr.active ? "yes" : "no"}</td></tr>;
            })}</tbody>
          </table>
        </div>
      )}
    </>
  );
}

function LedgerTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/lead-exchange/ledger").then((r) => setRows(r.data)); }, []);
  return (
    <>
      {rows.length === 0 ? (
        <EmptyState testId="lx-ledger-empty">LEDGER IS EMPTY.</EmptyState>
      ) : (
        <div className="dt6-card overflow-hidden">
          <table className="dt6-table">
            <thead><tr><th>When</th><th>Lead</th><th>Buyer</th><th>Price</th><th>Status</th></tr></thead>
            <tbody>{rows.map((e)=>(
              <tr key={e.id} data-testid={`lx-ledger-row-${e.id}`}>
                <td className="font-mono text-xs">{new Date(e.created_at).toLocaleString()}</td>
                <td className="font-mono text-xs">{String(e.lead_id).slice(0,8)}</td>
                <td className="text-xs">{e.buyer_name || String(e.buyer_id).slice(0,8)}</td>
                <td className="font-mono">{e.price ? `$${e.price}` : "—"}</td>
                <td><span className="dt6-chip dt6-chip-amber">{e.status}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </>
  );
}
